/**
 * MilesValueCard — "Should you use miles?" (register P8). The traveler types what the
 * airline showed them; the server does the sum, so this card only says it in words.
 * Owner's page only: the API answers 404 to anyone else, and then this renders nothing.
 */
import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { tripsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { MilesQuote, MilesQuoteInput, MilesQuoteKind, MilesQuotesData } from "@/lib/types";

const TITLE = "Should you use miles?";
const NO_YARDSTICK = "We do not have a typical value for this program, so no verdict.";
const NOT_KNOWN_EARN = "Whether the miles part earns points is not known.";

const KIND: Record<MilesQuoteKind, string> = { cash_and_miles: "Cash and miles", award: "Award ticket" };

const money = (n: number) => `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const roughly = (n: number) => `$${Math.round(Math.abs(n)).toLocaleString("en-US")}`;
const count = (n: number) => n.toLocaleString("en-US");
const cents = (n: number) => `${n.toFixed(2)}¢`;
const quotedDay = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

/** The lead sentence and the working behind it. Null when the quote has no verdict. */
export function readWords(q: MilesQuote): { lead: string; why: string } | null {
  const v = q.value;
  if (v.verdict == null || v.typicalCentsPerMile == null || v.milesWorth == null || v.valueDifference == null) return null;
  const yardstick = `At the ${v.typicalCentsPerMile}¢ a mile we treat as typical for ${q.program}, those miles are worth about ${roughly(v.milesWorth)}`;
  const saves = `${count(q.miles)} miles would save you ${money(v.cashSaved)}.`;
  const gap = Math.abs(v.valueDifference);
  if (v.verdict === "pay_cash") {
    return {
      lead: `Pay cash. ${KIND[q.kind]} gets you only ${cents(v.centsPerMile)} a mile.`,
      why: `${saves} ${yardstick}, so you would give up about ${roughly(gap)} of value.`,
    };
  }
  return {
    lead: `Use miles. ${KIND[q.kind]} gets you ${cents(v.centsPerMile)} a mile.`,
    why: gap < 1 ? `${saves} ${yardstick}, about the same.` : `${saves} ${yardstick}, so you would come out about ${roughly(gap)} ahead.`,
  };
}

/** What else it costs: only what we know, and that the points question is open. */
export function otherCosts(q: MilesQuote, balances: Record<string, number>): { bold: string; rest: string }[] {
  const out: { bold: string; rest: string }[] = [];
  if (q.canChange === false) {
    out.push({ bold: "This trip cannot be changed.", rest: q.canChangeNote ? `You noted: ${q.canChangeNote}` : "You told us the airline said so." });
  }
  out.push({ bold: "Points:", rest: NOT_KNOWN_EARN });
  const balance = balances[q.program];
  if (balance !== undefined) {
    const after = balance - q.miles;
    out.push({
      bold: "Your balance:",
      rest:
        after >= 0
          ? `falls from ${count(balance)} to ${count(after)} ${q.program} miles.`
          : `you have ${count(balance)} ${q.program} miles, ${count(-after)} short of this.`,
    });
  }
  return out;
}

export interface MilesFormState {
  kind: MilesQuoteKind;
  program: string;
  cashTotal: string;
  miles: string;
  copay: string;
  partySize: string;
  cannotChange: boolean;
  note: string;
}

/** What the form holds, as text, to what the API takes. Null when it is not ready to send. */
export function quoteFromForm(f: MilesFormState): MilesQuoteInput | null {
  const num = (s: string) => Number(s.replace(/[$,\s]/g, ""));
  if (!f.program.trim() || !f.cashTotal.trim() || !f.miles.trim() || !f.partySize.trim()) return null;
  const cashTotal = num(f.cashTotal);
  const miles = num(f.miles);
  const copay = f.copay.trim() === "" ? 0 : num(f.copay);
  const partySize = num(f.partySize);
  if (![cashTotal, miles, copay, partySize].every(Number.isFinite)) return null;
  return {
    kind: f.kind,
    program: f.program.trim(),
    cashTotal,
    miles,
    copay,
    partySize,
    canChange: f.cannotChange ? false : null,
    canChangeNote: f.cannotChange ? f.note.trim() || null : null,
  };
}

/** Prefill only what is real: a total the airline quoted on a hold, and the trip's travelers. */
export function emptyForm(data: Pick<MilesQuotesData, "prefill" | "milesBalance">): MilesFormState {
  const programs = Object.keys(data.milesBalance);
  return {
    kind: "cash_and_miles",
    program: programs.length === 1 ? programs[0] : "",
    cashTotal: data.prefill.cashTotal != null ? data.prefill.cashTotal.toFixed(2) : "",
    miles: "",
    copay: "",
    partySize: data.prefill.partySize != null ? String(data.prefill.partySize) : "",
    cannotChange: false,
    note: "",
  };
}

function QuoteForm({
  data,
  onAdd,
  saving,
  error,
}: {
  data: MilesQuotesData;
  onAdd: (q: MilesQuoteInput, done: () => void) => void;
  saving?: boolean;
  error?: string | null;
}) {
  const [f, setF] = useState<MilesFormState>(() => emptyForm(data));
  const edit = (patch: Partial<MilesFormState>) => setF((s) => ({ ...s, ...patch }));
  const ready = quoteFromForm(f);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ready) onAdd(ready, () => setF(emptyForm(data)));
  };
  return (
    <form onSubmit={submit} className="mt-3 space-y-3 border-t pt-3" aria-label="Add what the airline showed you">
      <div>
        <p className="text-sm font-medium text-foreground">Add what the airline showed you</p>
        <p className="text-xs text-muted-foreground">Copy the totals for everyone from the checkout page.</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="miles-kind">Kind</Label>
          <select
            id="miles-kind"
            className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground"
            value={f.kind}
            onChange={(e) => edit({ kind: e.target.value as MilesQuoteKind })}
          >
            <option value="cash_and_miles">Cash and miles</option>
            <option value="award">Award ticket</option>
          </select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="miles-program">Program</Label>
          <Input id="miles-program" className="h-11" placeholder="AAdvantage" maxLength={40} value={f.program} onChange={(e) => edit({ program: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="miles-cash">Cash price, everyone</Label>
          <Input id="miles-cash" className="h-11" inputMode="decimal" value={f.cashTotal} onChange={(e) => edit({ cashTotal: e.target.value })} />
          {data.prefill.cashSource === "hold" && <p className="text-[11px] text-muted-foreground">From the total the airline quoted on your hold.</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="miles-party">Travelers</Label>
          <Input id="miles-party" className="h-11" inputMode="numeric" value={f.partySize} onChange={(e) => edit({ partySize: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="miles-miles">Miles, everyone</Label>
          <Input id="miles-miles" className="h-11" inputMode="numeric" value={f.miles} onChange={(e) => edit({ miles: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="miles-copay">Cash you still pay</Label>
          <Input id="miles-copay" className="h-11" inputMode="decimal" value={f.copay} onChange={(e) => edit({ copay: e.target.value })} />
        </div>
      </div>
      <label className="flex min-h-11 items-center gap-2 text-sm text-foreground">
        <input type="checkbox" className="h-5 w-5 shrink-0" checked={f.cannotChange} onChange={(e) => edit({ cannotChange: e.target.checked })} />
        The airline says this trip cannot be changed
      </label>
      {f.cannotChange && (
        <div className="space-y-1.5">
          <Label htmlFor="miles-note">What it said (optional)</Label>
          <Input id="miles-note" className="h-11" maxLength={200} value={f.note} onChange={(e) => edit({ note: e.target.value })} />
        </div>
      )}
      <Button type="submit" className="min-h-11" disabled={!ready || saving}>
        {saving ? "Adding" : "Add"}
      </Button>
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </form>
  );
}

export function MilesValueView({
  data,
  onAdd = () => {},
  onDelete = () => {},
  saving,
  error,
}: {
  data: MilesQuotesData;
  onAdd?: (q: MilesQuoteInput, done: () => void) => void;
  onDelete?: (id: string) => void;
  saving?: boolean;
  error?: string | null;
}) {
  const read = data.quotes.find((q) => q.id === data.readQuoteId) ?? null;
  const words = read ? readWords(read) : null;
  // With no verdict, the newest quote still shows its value and what else it costs.
  const focus = read ?? data.quotes[0] ?? null;
  const cash = focus?.cashTotal ?? null;
  const noYardstick = data.quotes.some((q) => q.value.verdict == null);

  return (
    <section className="rounded-xl border bg-card p-4 shadow-paper" aria-label={TITLE}>
      <h3 className="text-sm font-medium text-foreground">{TITLE}</h3>
      {data.quotes.length === 0 ? (
        <p className="mt-1 text-xs text-muted-foreground">
          If the airline offers to take miles, add what it showed you and we will work out what your miles are getting you.
        </p>
      ) : (
        <>
          {words && read && (
            <div className="mt-3 rounded-lg border border-teal-200 bg-teal-50 p-3 text-foreground dark:border-teal-800 dark:bg-teal-950/40">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Our read</p>
              <p className="mt-1 text-[15px] font-semibold leading-snug">{words.lead}</p>
              <p className="mt-1.5 text-[13px] leading-relaxed">{words.why}</p>
              <p className="mt-1.5 text-[11px] text-muted-foreground">
                For {read.partySize}. You typed this on {quotedDay(read.quotedAt)}. The {read.value.typicalCentsPerMile}¢ is a typical figure for {read.program}, not a promise.
              </p>
            </div>
          )}

          <div className="mt-3 overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="text-[11px] text-muted-foreground">
                  <th scope="col" className="pb-1 text-left font-normal">
                    <span className="sr-only">Way to pay</span>
                  </th>
                  <th scope="col" className="pb-1 pl-2 text-right font-normal">You pay</th>
                  <th scope="col" className="pb-1 pl-2 text-right font-normal">Value a mile</th>
                  <th scope="col" className="pb-1">
                    <span className="sr-only">Remove</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t font-semibold">
                  <td className="py-2 pr-2">Cash</td>
                  <td className="whitespace-nowrap py-2 pl-2 text-right">{cash != null ? money(cash) : "Not known"}</td>
                  <td className="py-2 pl-2 text-right">
                    <span aria-label="Does not apply">n/a</span>
                  </td>
                  <td />
                </tr>
                {data.quotes.map((q) => (
                  <tr key={q.id} className="border-t">
                    <td className="py-2 pr-2">
                      {KIND[q.kind]}
                      <span className="block text-[11px] text-muted-foreground">
                        {q.program}
                        {q.cashTotal !== cash ? `, against ${money(q.cashTotal)} cash` : ""}
                      </span>
                    </td>
                    <td className="py-2 pl-2 text-right">
                      <span className="block whitespace-nowrap">{count(q.miles)} mi</span>
                      <span className="block whitespace-nowrap">+ {money(q.copay)}</span>
                    </td>
                    <td className="py-2 pl-2 text-right">{cents(q.value.centsPerMile)}</td>
                    <td className="py-1 pl-1 text-right">
                      <button
                        type="button"
                        className="min-h-11 min-w-11 rounded text-xs text-muted-foreground underline"
                        aria-label={`Remove ${KIND[q.kind]} on ${q.program}`}
                        onClick={() => onDelete(q.id)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {noYardstick && <p className="mt-2 text-xs text-muted-foreground">{NO_YARDSTICK}</p>}

          {focus && (
            <div className="mt-3 border-t pt-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">What else it costs</p>
              <ul className="mt-1 space-y-1 text-[13px] leading-relaxed">
                {otherCosts(focus, data.milesBalance).map((c) => (
                  <li key={c.bold}>
                    <strong>{c.bold}</strong> {c.rest}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
      <QuoteForm key={`${data.prefill.cashTotal}|${data.prefill.partySize}`} data={data} onAdd={onAdd} saving={saving} error={error} />
    </section>
  );
}

export default function MilesValueCard({ tripId }: { tripId: string }) {
  const client = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["milesQuotes", tripId],
    queryFn: () => tripsApi.milesQuotes(tripId),
    retry: false,
  });
  const refresh = () => client.invalidateQueries({ queryKey: ["milesQuotes", tripId] });
  const add = useMutation({ mutationFn: (q: MilesQuoteInput) => tripsApi.addMilesQuote(tripId, q), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (id: string) => tripsApi.deleteMilesQuote(tripId, id), onSuccess: refresh });

  if (isLoading) {
    return (
      <section className="rounded-xl border bg-card p-4 shadow-paper">
        <h3 className="text-sm font-medium text-foreground">{TITLE}</h3>
        <p className="mt-1 text-xs text-muted-foreground">Loading&hellip;</p>
      </section>
    );
  }
  if (isError) {
    // Not the owner: this card is not for them.
    if ((error as { response?: { status?: number } })?.response?.status === 404) return null;
    return (
      <section className="rounded-xl border bg-card p-4 shadow-paper">
        <h3 className="text-sm font-medium text-foreground">{TITLE}</h3>
        <p className="mt-1 text-xs text-muted-foreground">We could not load this just now.</p>
      </section>
    );
  }
  if (!data) return null;
  const failure = add.error ?? remove.error;
  const failureText = failure ? ((failure as { response?: { data?: { error?: string } } }).response?.data?.error ?? "That did not save. Try again.") : null;
  return (
    <MilesValueView
      data={data}
      saving={add.isPending}
      error={failureText}
      onAdd={(q, done) => add.mutate(q, { onSuccess: done })}
      onDelete={(id) => remove.mutate(id)}
    />
  );
}
